import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Button, Input } from '@treezor/design-system';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { getPasswordStrategy, setPasswordStrategy } from '@/store/actions/password-config.action';

import { IPasswordStrategy, IStrategyPasswordForm } from '@/store/types/password-config.types';

import { SecuritySkeleton } from '@/pages/admin/configuration/SecuritySkeleton';
import { Switch } from '@/ui-kit/switch/Switch';

export const Security = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const passwordStrategy = useAppSelector((state) => state.passwordStrategy.passwordStrategy);
  const isLoading = useAppSelector((state) => state.passwordStrategy.isLoading);

  useEffect(() => {
    dispatch(getPasswordStrategy());
  }, [dispatch]);

  const defaultValues: IStrategyPasswordForm = {
    requireLetters: Number(passwordStrategy?.passwordPattern?.requireLetters),
    requireCaseDiff: Number(passwordStrategy?.passwordPattern?.requireCaseDiff),
    requireNumbers: Number(passwordStrategy?.passwordPattern?.requireNumbers),
    requireSpecialCharacter: Number(passwordStrategy?.passwordPattern?.requireSpecialCharacter),
    minLength: Number(passwordStrategy?.passwordPattern?.minLength),
  };

  const { handleSubmit, watch, register, setValue } = useForm<IStrategyPasswordForm>({ defaultValues });

  const options = [
    {
      name: 'requireLetters',
      label: t('pages.administration.configuration.security.options.description.requireLetters'),
      description: t('pages.administration.configuration.security.options.patternDetail.requireLetters'),
    },
    {
      name: 'requireCaseDiff',
      label: t('pages.administration.configuration.security.options.description.requireCaseDiff'),
      description: t('pages.administration.configuration.security.options.patternDetail.requireCaseDiff'),
    },
    {
      name: 'requireNumbers',
      label: t('pages.administration.configuration.security.options.description.requireNumbers'),
      description: t('pages.administration.configuration.security.options.patternDetail.requireNumbers'),
    },
    {
      name: 'requireSpecialCharacter',
      label: t('pages.administration.configuration.security.options.description.requireSpecialCharacter'),
      description: t('pages.administration.configuration.security.options.patternDetail.requireSpecialCharacter'),
    },
  ];

  const onSubmit = (data: IStrategyPasswordForm) => {
    const minLength = !Number.isNaN(data?.minLength) ? Number(data?.minLength) : 12;

    const passwordConfig: IPasswordStrategy = {
      passwordChangeRedirectUrl: passwordStrategy.passwordChangeRedirectUrl,
      passwordPattern: {
        requireLetters: Boolean(data.requireLetters),
        requireCaseDiff: Boolean(data.requireCaseDiff),
        requireNumbers: Boolean(data.requireNumbers),
        requireSpecialCharacter: Boolean(data.requireSpecialCharacter),
        minLength,
      },
    };
    dispatch(setPasswordStrategy(passwordConfig));
  };

  return (
    <div className="w-auto border bg-primary border-primary rounded-100 py-9 px-8 mt-5 mb-3 mx-3 h-fit relative">
      {isLoading ? (
        <SecuritySkeleton />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-4">
            <h5 className="typo-h5-bold text-secondary">{t('pages.administration.configuration.security.title')}</h5>

            <Button type="submit" id="update-pwd-strategy-btn">
              {t('pages.administration.configuration.security.forms.saveBtn')}
            </Button>
          </div>

          <div className="flex flex-col gap-6 mt-6">
            <div className="flex items-center">
              <Input
                type="number"
                label={t('pages.administration.configuration.security.forms.description')}
                placeholder={t('pages.administration.configuration.security.forms.minLength')}
                value={watch('minLength').toString()}
                {...register('minLength')}
              />
            </div>
            <div className="flex flex-col gap-2">
              {options.map((option) => (
                <Switch
                  key={option.name}
                  isEnabled={watch(option.name as keyof IStrategyPasswordForm) === 1}
                  label={option.label}
                  hint={option.description}
                  handleChange={(value) => setValue(option.name as keyof IStrategyPasswordForm, value === true ? 1 : 0)}
                />
              ))}
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
